/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useEffect, useRef } from "react";
import Files from "./FilemanagerFiles";
import LeftSideTree from "./FileManagerLeftSideTree";
import Toolbar from "./FileManagerToolbar";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import { CircularProgress } from "@mui/material";
import useFileManagerComponent from "./useFileManagerComponent";

export interface ContextMenuItem {
  id: string;
  name: string;
  icon?: string;
  onClick?: (e: MouseEvent, payload?: unknown) => void;
}

interface FileManagerComponentProps {
  fileManagerData: any;
  allowDragAndDropUpload: boolean;
  onFileClick: Function;
  onFileSelect: Function;
  setCurrentFolder: Function;
  toolbarItems: any;
  toolbarClick: Function;
  contextMenuSettings?: {
    file: ContextMenuItem[];
    folder: ContextMenuItem[];
    layout: ContextMenuItem[];
    visible: boolean;
  };
  menuOpen: Function;
  fileColumns: any;
  setFileManagerData: Function;
  beforeSending: Function;
  onSuccess: Function;
  onFileLoad: Function;
  showFolderTree: boolean;
  path: string;
  allowViewFileOptions: boolean;
  disableDragAndDropUpload: boolean;
  preSearchText?: string;
}

const FileManagerComponent = forwardRef(
  (
    {
      fileManagerData = {
        cwd: {},
        files: [],
      },
      allowDragAndDropUpload = false,
      onFileClick = () => {},
      onFileSelect = () => {},
      setCurrentFolder = () => {},
      toolbarItems = [],
      toolbarClick,
      contextMenuSettings = {
        file: [],
        folder: [],
        layout: [],
        visible: true,
      },
      menuOpen = () => {},
      fileColumns = [],
      setFileManagerData,
      beforeSending = () => {},
      onSuccess = () => {},
      onFileLoad = () => {},
      showFolderTree = true,
      path,
      allowViewFileOptions = false,
      disableDragAndDropUpload,
      preSearchText,
    }: FileManagerComponentProps,
    ref,
  ) => {
    const tempRef = useRef<any>({});
    const filesRef: any = ref || tempRef;
    const treeViewRef = useRef(null);

    const {
      // State
      selectedFolder,
      setSelectedFolder,
      filesViewMode,
      setFilesViewMode,
      tempFolder,
      refreshGrid,
      selectedFilesInIconsMode,
      setSelectedFilesInIconsMode,
      isViewRendered,
      setIsViewRendered,
      isLoaded,

      // Refs
      splitterSizeRef,

      // Handlers
      refreshFileManager,
      refreshFiles,
      handleSearchFiles,
      getSelectedFilesInFileManager,
      showLoading,
      hideLoading,
    } = useFileManagerComponent({
      fileManagerData,
      setFileManagerData,
      setCurrentFolder,
      beforeSending,
      onSuccess,
      showFolderTree,
      path,
    });

    // Expose methods via ref
    useEffect(() => {
      if (filesRef.current) {
        filesRef.current.refreshFileManager = refreshFileManager;
        filesRef.current.refreshFiles = async () => {
          const selectedRows = filesRef.current?.slickGrid?.getSelectedRows() || [];
          filesRef.current._pendingSelectedRows = selectedRows;
          await refreshFiles();
        };
        filesRef.current.filesViewMode = filesViewMode;
        filesRef.current.getSelectedFilesInFileManager = () => getSelectedFilesInFileManager(filesRef);
        filesRef.current.showLoading = showLoading;
        filesRef.current.hideLoading = hideLoading;
      }
    }, [filesRef.current, isViewRendered, beforeSending, onSuccess, filesViewMode, selectedFilesInIconsMode]);

    const getContent = () => {
      return (
        <div className="h-full flex flex-col w-full relative">
          {refreshGrid && (
            <div className="absolute h-full w-full flex justify-center items-center z-20 rounded-md">
              <CircularProgress />
            </div>
          )}
          <Toolbar
            setFilesViewMode={setFilesViewMode}
            filesViewMode={filesViewMode}
            allowViewFileOptions={allowViewFileOptions}
            toolbarClick={toolbarClick}
            toolbarItems={toolbarItems}
          />
          <SplitLayout
            initialSizes={splitterSizeRef.current}
            gutterWidth={"1px"}
            gutterStyle={{ position: "absolute", zIndex: 50 }}
            minSizes={[20, 45]}
            style={{ display: "flex", height: "100%" }}
            showProperties={true}
          >
            {[
              showFolderTree && (
                <div
                  key="folderTree"
                  className="flex h-full border border-s-0 border-t-0 border-b-0 border-e-button_light overflow-auto"
                >
                  <LeftSideTree
                    ref={treeViewRef}
                    onFileLoad={onFileLoad}
                    fileManagerData={fileManagerData}
                    selectedFolder={selectedFolder}
                    setSelectedFolder={setSelectedFolder}
                  />
                </div>
              ),
              <div key="filesGrid" className="h-full flex">
                <Files
                  ref={filesRef}
                  preSearchText={preSearchText}
                  setIsViewRendered={setIsViewRendered}
                  isViewRendered={isViewRendered}
                  filesViewMode={filesViewMode}
                  setSelectedFilesInIconsMode={setSelectedFilesInIconsMode}
                  selectedFilesInIconsMode={selectedFilesInIconsMode}
                  allowDragAndDropUpload={allowDragAndDropUpload}
                  onFileLoad={onFileLoad}
                  tempFolder={tempFolder}
                  beforeSending={beforeSending}
                  handleSearchFiles={handleSearchFiles}
                  fileColumns={fileColumns}
                  menuOpen={menuOpen}
                  contextMenuSettings={contextMenuSettings}
                  fileManagerData={fileManagerData}
                  onFileSelect={onFileSelect}
                  onFileClick={onFileClick}
                  selectedFolder={selectedFolder}
                  setSelectedFolder={setSelectedFolder}
                  disableDragAndDropUpload={disableDragAndDropUpload}
                />
              </div>,
            ].filter(Boolean)}
          </SplitLayout>
        </div>
      );
    };

    return (
      <div className="w-full h-full items-center justify-center overflow-auto flex flex-col">
        {isLoaded ? getContent() : <CircularProgress />}
      </div>
    );
  },
);

FileManagerComponent.displayName = "FileManagerComponent";
export default FileManagerComponent;
